import EmailIcon from "@mui/icons-material/Email";
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Link,
  Skeleton,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";

import mixpanel from "../../utils/mixpanel";
import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";

export type KeyContactDetails = {
  email: string;
  avatarURL?: string;
  name: string;
  job: string;
};

type Props = {
  details: KeyContactDetails | null;
  sx?: SxProps<Theme>;
};

function hasDetails(details: KeyContactDetails | null): details is KeyContactDetails {
  return details !== null;
}

function extractInitials(name: string) {
  return name
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .substring(0, 2);
}

export const KeyContact = ({ details, sx }: Props) => {
  const isLoading = !hasDetails(details);
  const { trackEvent } = useSegmentTrackEvent();

  const handleEmailClick = () => {
    // Track mixpanel event (existing)
    mixpanel.track("home.contacts.email.click", { $referrer: location.href });

    // Track segment event
    trackEvent("Key contacts", {
      source: "Home Dashboard",
      feature: "Home Dashboard",
      target_internal_destination_description: "Contact email click",
      contactEmail: details?.email,
      contactName: details?.name,
    });
  };

  return (
    <Card
      sx={{ display: "flex", flexDirection: "column", ...sx }}
      data-cy={isLoading ? "key-contact-card-loading" : `key-contact-card`}
    >
      <CardHeader
        avatar={
          isLoading ? (
            <Skeleton variant="circular" width={40} height={40} />
          ) : (
            <Avatar src={details.avatarURL}>{extractInitials(details.name)}</Avatar>
          )
        }
        title={isLoading ? <Skeleton /> : details.name}
        slotProps={{
          title: { variant: "subtitle1" },
          subheader: { variant: "body2" },
        }}
        subheader={isLoading ? <Skeleton /> : details.job}
        sx={{ flex: 1, alignItems: "flex-start" }}
      />
      <CardContent sx={{ pt: 0, pl: 9 }}>
        {isLoading ? (
          <Skeleton />
        ) : (
          <Link
            href={`mailto:${details.email}`}
            color="inherit"
            target="_blank"
            onClick={handleEmailClick}
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <EmailIcon color="primary" sx={{ mr: 0.5 }} fontSize="small" />
            <Typography
              color="primary"
              variant="body2"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", textWrap: "nowrap" }}
            >
              {details.email}
            </Typography>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};
